import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    FONT,
    BTN_MD,
    BTN_MD_SECONDARY,
    Heading,
    MarketingHeader,
    Seo,
} from '../components/marketing/MarketingKit';

// ─────────────────────────────────────────────────────────────────────────────
// /login, the public sign-in screen: a two-step email then password form on the
// left, the brand film on the right. Cognito does the authenticating; the
// hosted-UI leg for Google returns here with a code that AuthProvider spends.
//
// This is a public URL, so the chrome follows the marketing design language:
// monochrome, hairline fields that darken on focus, pill buttons at the kit
// heights, Switzer type. The only color on the page is the Google mark, which
// is a third-party logo and has to stay as it is drawn.
// ─────────────────────────────────────────────────────────────────────────────

// Fields: 16px radius, hairline border, darkening on focus. Same shape as the
// signup wizard so the two auth screens read as one.
const INPUT = 'w-full rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white px-[18px] py-[15px] text-[15px] text-[#0A0A0A] placeholder-[#A1A1A1] outline-none transition-colors focus:border-[rgba(10,10,10,0.35)]';
const PRIMARY = BTN_MD + ' w-full disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY = BTN_MD_SECONDARY + ' w-full disabled:cursor-not-allowed disabled:opacity-50';
const NOTE = 'rounded-[16px] bg-[#F5F5F5] px-[18px] py-[14px] text-[13px] leading-[1.55] text-[#6B6B6B]';

const SITE = 'https://wavelengtherp.com';
const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebPage',
            '@id': `${SITE}/login#webpage`,
            url: `${SITE}/login`,
            name: 'Sign in to Wavelength',
            description: 'Sign in to a Wavelength workspace to close the day, reconcile takings against deposits and run every location from one back office.',
            isPartOf: { '@id': `${SITE}/#organization` },
            publisher: { '@id': `${SITE}/#organization` },
        },
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/overview` },
                { '@type': 'ListItem', position: 2, name: 'Sign in', item: `${SITE}/login` },
            ],
        },
    ],
};

function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.88 2.68-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
        </svg>
    );
}

function LoginPage() {
    const { login, loginWithGoogle, oauthReturn, clearOAuthReturn, completeNewPassword, pendingChallenge } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || '/';

    const [step, setStep]               = useState('email'); // 'email' | 'password'
    const [email, setEmail]             = useState('');
    const [password, setPassword]       = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNew, setConfirmNew]   = useState('');
    const [error, setError]             = useState('');
    const [loading, setLoading]         = useState(false);
    const [showPw, setShowPw]           = useState(false);
    // True from the click on "Continue with Google" until the browser has left.
    const [leavingForGoogle, setLeavingForGoogle] = useState(false);

    const passwordRef = useRef(null);

    const inChallenge = !!pendingChallenge;

    // ── "Continue with Google" return leg ─────────────────────────────────────
    // AuthProvider exchanges /login?code=… for a session (it lives above the
    // store-keyed tree, so this page remounting mid-flight can't interrupt it);
    // we just reflect its progress and finish with the navigation.
    const oauthBusy = leavingForGoogle || oauthReturn.status === 'busy';
    useEffect(() => {
        if (oauthReturn.status === 'done') {
            clearOAuthReturn();
            navigate(oauthReturn.from || from, { replace: true });
        } else if (oauthReturn.status === 'error') {
            setError(oauthReturn.error);
            // Drop the spent ?code / ?error from the address bar.
            if (location.search) navigate('/login', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oauthReturn]);

    async function handleGoogle() {
        setError('');
        clearOAuthReturn();
        setLeavingForGoogle(true);
        const res = await loginWithGoogle({ from });
        if (!res.ok) {
            setError(res.error || 'Google sign-in isn’t enabled yet. Continue with your work email below.');
            setLeavingForGoogle(false);
        }
        // On success the browser is already navigating away.
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        if (oauthReturn.status !== 'idle') clearOAuthReturn();

        // First-time admin-created user setting a permanent password.
        if (inChallenge) {
            if (!newPassword || newPassword.length < 8) { setError('New password must be at least 8 characters.'); return; }
            if (newPassword !== confirmNew)             { setError('Passwords do not match.'); return; }
            setLoading(true);
            const res = await completeNewPassword(newPassword);
            if (res.ok) { navigate(from, { replace: true }); }
            else { setError(res.error || 'Could not set new password.'); setLoading(false); }
            return;
        }

        // Step 1, collect the work email, then reveal the password field.
        if (step === 'email') {
            const value = email.trim();
            if (!/\S+@\S+\.\S+/.test(value)) { setError('Enter a valid email address.'); return; }
            setStep('password');
            setTimeout(() => passwordRef.current?.focus(), 0);
            return;
        }

        // Step 2, authenticate against Cognito.
        if (!password) { setError('Enter your password.'); return; }
        setLoading(true);
        const res = await login(email.trim(), password);
        if (res.ok) { navigate(from, { replace: true }); return; }
        if (res.challenge === 'NEW_PASSWORD_REQUIRED') { setError(''); setLoading(false); return; }
        setError(res.error || 'Sign-in failed.');
        setLoading(false);
    }

    const heading = inChallenge ? 'Set a new password.' : 'Welcome back.';
    const subline = inChallenge ? '' : 'One workspace for every location.';

    return (
        <div className="flex min-h-screen w-full flex-col bg-white text-[#0A0A0A] antialiased" style={{ fontFamily: FONT }}>
            <Seo
                title="Sign in"
                description="Sign in to your Wavelength workspace to close the day, count the drawer, match takings to processor payouts and bank deposits, and run every location at once."
                path="/login"
                keywords="wavelength login, back office sign in, retail reconciliation software login, multi-location dashboard"
                jsonLd={JSON_LD}
            />

            {/* ─── shared marketing header (same as every public page) ─── */}
            <MarketingHeader />

            {/* ─── body: left auth column + right showcase ─── */}
            <div className="flex w-full flex-1 flex-col lg:flex-row lg:pt-2">

                {/* LEFT: auth */}
                <main className="flex w-full items-center justify-center px-6 pb-16 pt-2 lg:w-1/2">
                    <div className="flex w-full max-w-[400px] flex-col items-center">

                        <Heading as="h1" className="text-center">
                            {heading}
                            {subline && <><br /><span className="text-[#A1A1A1]">{subline}</span></>}
                        </Heading>

                        <p className="mt-4 max-w-[300px] text-center text-[14px] leading-[1.55] text-[#6B6B6B]">
                            {inChallenge
                                ? 'This is your first sign-in. Choose a permanent password to continue.'
                                : oauthBusy
                                    ? 'Finishing your Google sign-in.'
                                    : 'Sign in with the email your workspace was set up with.'}
                        </p>

                        {/* Hosted-UI leg in flight (leaving for Google, or back with a code) */}
                        {oauthBusy && !inChallenge && (
                            <div className="mt-8 flex w-full flex-col items-center gap-4 rounded-[16px] bg-[#F5F5F5] px-[18px] py-8" role="status" aria-live="polite">
                                <span className="h-6 w-6 animate-spin rounded-full border-2 border-[rgba(10,10,10,0.15)] border-t-[#0A0A0A]" />
                                <p className="flex items-center gap-2 text-[14px] text-[#0A0A0A]">
                                    <GoogleIcon />
                                    Signing you in with Google…
                                </p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className={'mt-8 flex w-full flex-col gap-3' + (oauthBusy && !inChallenge ? ' hidden' : '')}>

                            {!inChallenge && (
                                <>
                                    <button type="button" onClick={handleGoogle} disabled={oauthBusy} className={SECONDARY}>
                                        <GoogleIcon />
                                        Continue with Google
                                    </button>

                                    <div className="flex items-center gap-4 py-1">
                                        <span className="h-px flex-1 bg-[rgba(10,10,10,0.10)]" />
                                        <span className="text-[13px] text-[#A1A1A1]">or</span>
                                        <span className="h-px flex-1 bg-[rgba(10,10,10,0.10)]" />
                                    </div>

                                    <input
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        readOnly={step === 'password'}
                                        placeholder="name@company.com"
                                        className={INPUT + (step === 'password' ? ' cursor-default bg-[#FAFAFA] text-[#6B6B6B]' : '')}
                                    />

                                    {step === 'email' && (
                                        <div className={NOTE}>
                                            <span className="font-medium text-[#0A0A0A]">Using your work email</span> will make it easier for you to collaborate with your team.
                                        </div>
                                    )}

                                    {step === 'password' && (
                                        <div className="relative">
                                            <input
                                                ref={passwordRef}
                                                type={showPw ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="Password"
                                                className={INPUT + ' pr-[64px]'}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPw(v => !v)}
                                                tabIndex={-1}
                                                className="absolute right-[16px] top-1/2 -translate-y-1/2 text-[12px] font-medium text-[#A1A1A1] transition-colors hover:text-[#0A0A0A]"
                                            >
                                                {showPw ? 'Hide' : 'Show'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {inChallenge && (
                                <>
                                    <div className={NOTE}>
                                        Signed in as <span className="font-medium text-[#0A0A0A]">{pendingChallenge.username}</span>
                                    </div>
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="New password, at least 8 characters"
                                        className={INPUT}
                                    />
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        value={confirmNew}
                                        onChange={(e) => setConfirmNew(e.target.value)}
                                        placeholder="Confirm new password"
                                        className={INPUT}
                                    />
                                </>
                            )}

                            {error && (
                                <p className="rounded-[12px] border border-[rgba(10,10,10,0.10)] bg-[#F5F5F5] px-4 py-2.5 text-[13px] leading-[1.5] text-[#0A0A0A]">
                                    {error}
                                </p>
                            )}

                            <button type="submit" disabled={loading} className={PRIMARY + ' mt-1'}>
                                {loading
                                    ? (inChallenge ? 'Saving…' : 'Signing in…')
                                    : inChallenge
                                        ? 'Set password and continue'
                                        : step === 'email' ? 'Continue' : 'Sign in'}
                            </button>

                            {!inChallenge && step === 'password' && (
                                <div className="flex items-center justify-between px-1 pt-1 text-[13px]">
                                    <button
                                        type="button"
                                        onClick={() => { setStep('email'); setPassword(''); setError(''); }}
                                        className="text-[#6B6B6B] transition-colors hover:text-[#0A0A0A]"
                                    >
                                        Use a different email
                                    </button>
                                    <Link to="/forgot-password" className="text-[#6B6B6B] transition-colors hover:text-[#0A0A0A]">
                                        Forgot password?
                                    </Link>
                                </div>
                            )}
                        </form>

                        <p className="mt-7 max-w-[330px] text-center text-[12px] leading-[1.6] text-[#A1A1A1]">
                            By signing in you agree to Wavelength's{' '}
                            <Link to="/terms" className="underline decoration-[rgba(10,10,10,0.25)] underline-offset-2 transition-colors hover:text-[#0A0A0A]">Terms</Link>,{' '}
                            <Link to="/privacy" className="underline decoration-[rgba(10,10,10,0.25)] underline-offset-2 transition-colors hover:text-[#0A0A0A]">Privacy Notice</Link>, and{' '}
                            <Link to="/privacy#cookies" className="underline decoration-[rgba(10,10,10,0.25)] underline-offset-2 transition-colors hover:text-[#0A0A0A]">Cookie Notice</Link>.
                        </p>

                        <p className="mt-5 text-center text-[13px] text-[#6B6B6B]">
                            No workspace yet?{' '}
                            <Link to="/signup" className="font-medium text-[#0A0A0A] underline decoration-[rgba(10,10,10,0.25)] underline-offset-2 transition-colors hover:decoration-[#0A0A0A]">Create one</Link>
                        </p>
                    </div>
                </main>

                {/* RIGHT: video showcase */}
                <div className="hidden p-3 lg:flex lg:w-1/2">
                    <div className="relative w-full overflow-hidden rounded-[28px] bg-[#111111]">
                        <video
                            className="absolute inset-0 h-full w-full object-cover"
                            src="/login-video.mp4"
                            poster="/login-poster.jpg"
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="auto"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
